import { nodeIncludeString, searchPruneTree } from 'util/tree';
import { groupDepartmentsByLevel } from 'util/groupDepartmentsByLevel';
import { selectQueryParam } from 'util/selector/routerSelector';
import { blockDepartmentTree } from 'util/blockDepartmentTree';

import { createSelector } from '@reduxjs/toolkit';
import { AppState } from 'reduxStore/appStore';
import { selectFiltersSearchText, selectReportSearchText } from 'reduxStore/search/selectors';
import { ComparisionMatrix } from 'model/ComparisionMatrix';
import { COMPARE_GROUPS_LIMIT_BOLD, COMPARE_GROUPS_LIMIT_DEFAULT } from 'register/ModeType';
import { Department } from 'model/Department';
import { LoadingStateEnum } from 'model/Resource';
import { selectSelectedClassifications } from 'reduxStore/demography/selectors';
import {
  selectHasMatrixAccess,
  selectHasUnrestrictedCrossFilteringPermission,
} from 'reduxStore/user/selectors';

import { STORE_NAME } from './initialState';

export const selectDomain = (state: AppState) => state[STORE_NAME];

export const selectDepartments = createSelector(
  selectDomain,
  (domain) => domain && domain.list.data
);

export const selectDepartmentsLoadingState = createSelector(
  selectDomain,
  (domain) => domain && domain.list.loadingState
);

export const selectSearchedDepartments = createSelector(
  selectDepartments,
  selectFiltersSearchText,
  (departments, query) => {
    if (!query?.trim()) {
      return departments;
    }
    return departments
      .map((department) => searchPruneTree(department, nodeIncludeString(query)))
      .filter((department) => department !== null) as Department[];
  }
);

/**
 * Returns searched departments with blocked property set based on filtering permissions
 * When user has demographics selected without unrestricted permission, all departments are blocked
 */
export const selectSearchedDepartmentsWithBlocking = createSelector(
  selectSearchedDepartments,
  selectHasUnrestrictedCrossFilteringPermission,
  selectSelectedClassifications,
  (departments, hasUnrestrictedPermission, selectedDemographics) => {
    // Users can filter by department if they have unrestricted permission or no demographics selected
    const canFilterByDepartment = hasUnrestrictedPermission || selectedDemographics.length === 0;

    if (canFilterByDepartment) {
      return departments;
    }
    return blockDepartmentTree(departments, true);
  }
);

export const selectSelectedDepartmentIds = createSelector(
  selectQueryParam('f'),
  (queryParam: any): number[] => {
    const departmentIds: string[] = Array.isArray(queryParam?.d)
      ? queryParam.d
      : Object.keys(queryParam?.d || {}).map((key) => queryParam.d[key]);

    return departmentIds.map((departmentId: string) => Number(departmentId));
  }
);

export const selectSelectedDepartments = createSelector(
  selectDomain,
  (domain) => domain && domain.selectedList
);

export const selectIsDepartmentSelected = (departmentId: number) =>
  createSelector(
    selectSelectedDepartmentIds,
    (selectedDepartmentsIds) => selectedDepartmentsIds.indexOf(departmentId) !== -1
  );

export const selectIsDepartmentIdSelected = (departmentId: number) =>
  createSelector(
    selectSelectedDepartments,
    (selectedDepartmentsIds) => selectedDepartmentsIds.indexOf(departmentId) !== -1
  );

export const selectReportSelectedDepartmentId = createSelector(
  selectQueryParam('f'),
  (queryParam: any) => queryParam && Number(queryParam.d)
);

export const selectDepartmentsFlatList = createSelector(
  selectDepartments,
  selectReportSelectedDepartmentId,
  (departments) => departments && Department.flattenDepartments(departments)
);

export const selectReportDepartment = createSelector(
  selectReportSelectedDepartmentId,
  selectDepartmentsFlatList,
  (departmentId, departments) =>
    departments && departments.find((department) => department.id === departmentId)
);

export const selectGroupedDepartmentsByLevel = createSelector(
  selectDepartments,
  (departments) => departments && groupDepartmentsByLevel(departments)
);

export const selectReportSearchedDepartments = createSelector(
  selectGroupedDepartmentsByLevel,
  selectReportSearchText,
  (departments, query) =>
    departments.map((level) =>
      level.filter((department) =>
        department.name.toLocaleLowerCase().trim().includes(query.toLocaleLowerCase().trim())
      )
    )
);

export const selectTableBreadth = createSelector(
  selectSelectedDepartmentIds,
  selectSelectedClassifications,
  (selectedDepartmentIds, selectedClassifications): ComparisionMatrix.Breadth => {
    const itemsLength = Math.max(selectedDepartmentIds.length, selectedClassifications.length);

    if (!itemsLength) {
      return 'bold';
    }

    if (itemsLength <= COMPARE_GROUPS_LIMIT_BOLD) {
      return 'bold';
    }

    if (itemsLength <= COMPARE_GROUPS_LIMIT_DEFAULT) {
      return 'default';
    }

    return 'narrow';
  }
);

export const selectSingleSelectedDepartmentId = createSelector(
  selectSelectedDepartmentIds,
  (selectedDepartmentIds) => {
    if (selectedDepartmentIds.length !== 1) {
      return undefined;
    }

    return selectedDepartmentIds[0];
  }
);

export const selectIsAllDepartmentsSelected = createSelector(
  selectDomain,
  (domain) => domain && domain.isAllDepartmentsSelected
);

export const selectSelectedDepartmentConnectedId = createSelector(
  selectSingleSelectedDepartmentId,
  selectDepartmentsFlatList,
  (departmentId, departments): number | null => {
    if (departmentId === undefined || !departments) return null;
    return departments.find((d) => d.id === departmentId)?.connectedId ?? null;
  }
);

export const selectSelectedDepartmentPath = createSelector(
  selectSingleSelectedDepartmentId,
  selectDepartments,
  (departmentId, departments): string[] =>
    departmentId === undefined ? [] : Department.findPath(departments, departmentId)
);

// Not yet resolved (or errored) — assume departments might exist so we never
// hide (or flash-hide) dependent CTAs before we actually know.
export const selectHasAnyDepartments = createSelector(
  selectDepartments,
  selectDepartmentsLoadingState,
  (departments, loadingState): boolean => {
    if (loadingState !== LoadingStateEnum.RESOLVED) return true;
    return (departments?.length ?? 0) > 0;
  }
);

export const selectHasValidDepartmentSelectionForActionPlan = createSelector(
  selectSelectedDepartmentIds,
  selectSelectedDepartmentConnectedId,
  selectHasMatrixAccess,
  (selectedDepartmentIds, connectedId, hasMatrixAccess): boolean => {
    if (selectedDepartmentIds.length > 1) return false;
    // Matrix users have no implicit department, so a plan can only be scoped
    // once they've explicitly picked exactly one.
    if (hasMatrixAccess) return selectedDepartmentIds.length === 1 && connectedId !== null;
    return selectedDepartmentIds.length === 0 || connectedId !== null;
  }
);
